import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { API_HOST } from '../../../../core/api/api.config';
import { ApiService } from '../../../../core/api/api.service';
import { ENDPOINTS } from '../../../../core/api/endpoints';

export interface RepaymentCalculationTrancheRow {
  quarter: string;
  month: string;
  prpsdDsbmntAmount: number;
  prpsdDsbmntSeq: number;
  installmentsByPeriod: Record<string, number>;
  /** Repayment term type (PKL_GRP.FACILITY_TYPE) this tranche belongs to. */
  rpymntTerm: number;
}

export interface RepaymentCalculationResponse {
  crdtPrpslSeq: number;
  trancheRows: RepaymentCalculationTrancheRow[];
  ttlDsbmnt: number;
}

export interface SaveRepaymentPlanTermPayload {
  trancheRows: RepaymentCalculationTrancheRow[];
  ttlDsbmnt: number;
  /** Repayment term type (PKL_GRP.FACILITY_TYPE) this group of tranche rows belongs to. */
  rpymntTerm: number;
}

export interface SaveRepaymentPlanPayload {
  crdtPrpslSeq: number;
  terms: SaveRepaymentPlanTermPayload[];
}

@Injectable({
  providedIn: 'root',
})
export class RepaymentPlanStateService {
  private readonly calculatedPlanSubject =
    new BehaviorSubject<RepaymentCalculationResponse | null>(null);

  readonly calculatedPlan$ =
    this.calculatedPlanSubject.asObservable();

  constructor(private readonly api: ApiService) {}

  get currentPlan(): RepaymentCalculationResponse | null {
    return this.calculatedPlanSubject.value;
  }

  setCalculatedPlan(
    plan: RepaymentCalculationResponse | null,
  ): void {
    this.calculatedPlanSubject.next(plan);
  }

  publishCalculationResponse(
    response: unknown,
  ): RepaymentCalculationResponse | null {
    const wrapper = response as { data?: unknown } | null;
    const source = (wrapper?.data ?? response) as Record<
      string,
      any
    > | null;

    if (!source || typeof source !== 'object') {
      return null;
    }

    const rawTrancheRows = this.extractRawTrancheRows(source);

    if (!rawTrancheRows) {
      return null;
    }

    const plan: RepaymentCalculationResponse = {
      crdtPrpslSeq: Number(source['crdtPrpslSeq'] ?? 0),

      ttlDsbmnt: this.toAmount(
        source['ttlDsbmnt'],
      ),

      trancheRows: rawTrancheRows.map(
        (row: Record<string, any>) => ({
          quarter: String(row?.['quarter'] ?? ''),

          month: String(
            row?.['month'] ?? '',
          ).slice(0, 10),

          prpsdDsbmntAmount: this.toAmount(
            row?.['prpsdDsbmntAmount'],
          ),

          prpsdDsbmntSeq: Number(
            row?.['prpsdDsbmntSeq'] ?? 0,
          ),

          installmentsByPeriod: this.normalizeSchedule(
            row?.['installmentsByPeriod'],
          ),

          rpymntTerm: Number(
            row?.['rpymntTerm'] ?? 0,
          ),
        }),
      ),
    };

    if (
      !Number.isFinite(plan.crdtPrpslSeq) ||
      plan.crdtPrpslSeq <= 0
    ) {
      return null;
    }

    this.calculatedPlanSubject.next(plan);

    return plan;
  }

  // New shape groups tranche rows by term: { termMatrices: [{ rpymntTerm,
  // trancheRows, ttlDsbmnt }] }. Falls back to the old flat `trancheRows`
  // shape (each row already carries its own `rpymntTerm`, so flattening
  // loses nothing).
  private extractRawTrancheRows(
    source: Record<string, any>,
  ): Record<string, any>[] | null {
    if (Array.isArray(source['termMatrices'])) {
      return source['termMatrices'].flatMap((term: any) =>
        Array.isArray(term?.trancheRows) ? term.trancheRows : [],
      );
    }

    if (Array.isArray(source['trancheRows'])) {
      return source['trancheRows'];
    }

    return null;
  }

  saveRepaymentPlan(
    payload: SaveRepaymentPlanPayload,
  ): Observable<unknown> {
    return this.api.post(
      API_HOST.repayment,
      ENDPOINTS.REPAYMENT.SAVE_PRPSD_RPYMNT,
      payload,
    );
  }

  private normalizeSchedule(
    value: unknown,
  ): Record<string, number> {
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value)
    ) {
      return {};
    }

    return Object.entries(
      value as Record<string, unknown>,
    )
      .filter(([period]) =>
        /^\d{4}-\d{2}-\d{2}$/.test(period),
      )
      .reduce<Record<string, number>>(
        (schedule, [period, amount]) => {
          schedule[period] = this.toAmount(amount);
          return schedule;
        },
        {},
      );
  }

  private toAmount(value: unknown): number {
    const amount = Number(value ?? 0);

    if (!Number.isFinite(amount) || amount < 0) {
      return 0;
    }

    return Math.round(
      (amount + Number.EPSILON) * 100,
    ) / 100;
  }
}